// Game events -> sound cues: the rules that decide which sound a game event makes, how loud it is
// and whether it is made at all. Pure functions and small state machines; the engine plays what
// they return (docs/AUDIO.md has the table).

import type { HitKind, HitStyle, MovementKind, ReloadPhase } from "../audio";
import { bombBeepInterval } from "../audio/sounds";
import { TICK_DT, wholeTicks } from "../net/constants";
import { PlayerFlags } from "../wasm/types";
import type { ProjectileSnapData, Vec3Like } from "../wasm/types";

// ---- Listener -----------------------------------------------------------------------------------

/**
 * The listener's forward vector for a view yaw / pitch (radians), written into `out`. Same
 * convention as the camera and the sim (yaw 0 looks along -Z, positive yaw turns left), so the
 * right-handed WebAudio listener (right = forward x up) has the player's right ear on +X at yaw 0.
 */
export function viewForward(yaw: number, pitch: number, out: Vec3Like): Vec3Like {
  const cp = Math.cos(pitch);
  out.x = -Math.sin(yaw) * cp;
  out.y = Math.sin(pitch);
  out.z = -Math.cos(yaw) * cp;
  return out;
}

// ---- Footsteps ----------------------------------------------------------------------------------

/**
 * Level of the local player's own footsteps. Far below an enemy's: a runner's own steps arrive
 * 1.7 m from the ear every 0.3 s and would bury the enemy steps they have to be heard over. At
 * this level an own step is no louder than an enemy running 15 m away.
 */
export const OWN_STEP_VOLUME = 0.12;

/** Own footsteps: only while running (walking and crouching are silent, DESIGN.md). */
export function ownStepVolume(walking: boolean, crouched: boolean): number {
  return walking || crouched ? 0 : OWN_STEP_VOLUME;
}

const QUIET_FLAGS = PlayerFlags.WALKING | PlayerFlags.CROUCHED | PlayerFlags.SLIDING | PlayerFlags.MANTLING;

/**
 * Footstep volume of another player from their snapshot flags: a running player on the ground is
 * heard at full volume (25 m); walking, crouching, sliding and mantling are silent (DESIGN.md:
 * "Footsteps carry 25 m when running; walking and crouching are silent").
 */
export function remoteStepVolume(flags: number): number {
  if ((flags & PlayerFlags.ALIVE) === 0 || (flags & PlayerFlags.ON_GROUND) === 0) return 0;
  return (flags & QUIET_FLAGS) !== 0 ? 0 : 1;
}

// ---- Hit confirms -------------------------------------------------------------------------------

export interface HitFacts {
  killed: boolean;
  head: boolean;
}

export interface HitPrefs {
  hitsound: "classic" | "tick" | "off";
  killsound: boolean;
}

/**
 * The confirm the shooter hears for a server `Hit`: a kill (with its own headshot variant) when
 * the kill sound is on, else the plain body or head hit unless the hit sound is off. The kill
 * confirm is not tied to the hit sound: it plays even with the hit sound off.
 */
export function hitCue(h: HitFacts, p: HitPrefs): { kind: HitKind; style: HitStyle } | null {
  const style: HitStyle = p.hitsound === "tick" ? "tick" : "classic";
  if (h.killed && p.killsound) return { kind: h.head ? "killHead" : "kill", style };
  if (p.hitsound === "off") return null;
  return { kind: h.head ? "head" : "body", style };
}

// ---- Damage taken -------------------------------------------------------------------------------

/**
 * Vector from the listener towards the damage source, for the engine to place the thud; undefined
 * when there is no source (bomb, world) or it is on top of the listener.
 */
export function damageDirection(from: Vec3Like | undefined, listener: Vec3Like): Vec3Like | undefined {
  if (!from) return undefined;
  const d = { x: from.x - listener.x, y: from.y - listener.y, z: from.z - listener.z };
  return Math.hypot(d.x, d.y, d.z) > 0.05 ? d : undefined;
}

// ---- Bomb ---------------------------------------------------------------------------------------

/** Bomb timer (DESIGN.md: 40 s). */
export const BOMB_TIMER_SEC = 40;

/** 0 at the plant, 1 at the explosion. */
export function bombUrgency(remainingSec: number, totalSec = BOMB_TIMER_SEC): number {
  return Math.min(1, Math.max(0, 1 - remainingSec / totalSec));
}

/**
 * Paces the bomb beeps from the time left: 1 s apart after the plant, speeding up to 0.12 s in the
 * last moment. `poll` every frame; it returns the urgency of the beep that is due, else null.
 */
export class BombBeeper {
  private next = 0;

  reset(): void {
    this.next = 0;
  }

  poll(nowMs: number, remainingSec: number, totalSec = BOMB_TIMER_SEC): number | null {
    if (remainingSec <= 0) return null;
    if (nowMs < this.next) return null;
    const urgency = bombUrgency(remainingSec, totalSec);
    const interval = bombBeepInterval(urgency) * 1000;
    // Keep the cadence across frame times, but never queue a burst after a hitch.
    const due = (this.next === 0 ? nowMs : this.next) + interval;
    this.next = due > nowMs ? due : nowMs + interval;
    return urgency;
  }
}

// ---- Countdown ----------------------------------------------------------------------------------

/** Seconds before a countdown or freeze ends in which each remaining second gets a beep. */
export const COUNTDOWN_BEEPS = 3;

/** One beep per second for the last `COUNTDOWN_BEEPS` seconds of the match countdown / round freeze. */
export class CountdownCue {
  private last = -1;

  reset(): void {
    this.last = -1;
  }

  poll(phase: string, endTick: number, nowTick: number, tickRate: number): boolean {
    if ((phase !== "countdown" && phase !== "round_freeze") || endTick <= 0) {
      this.last = -1;
      return false;
    }
    const sec = Math.ceil((endTick - nowTick) / tickRate);
    if (sec < 1 || sec > COUNTDOWN_BEEPS || sec === this.last) return false;
    this.last = sec;
    return true;
  }
}

// ---- Other players' movement and weapon handling ------------------------------------------------

/** Impact speed (m/s) from which a landing is heavy (the local prediction uses the same value). */
export const HEAVY_LAND_SPEED = 9;
/** Slower touchdowns (stepping off a kerb) make no sound. */
const MIN_LAND_SPEED = 3;
/** Upward speed that makes leaving the ground a jump. */
const MIN_JUMP_SPEED = 2;

const WALLRUN = PlayerFlags.WALLRUN_LEFT | PlayerFlags.WALLRUN_RIGHT;

/** What the previous frame of a remote player looked like, for edge detection. */
export interface MotionState {
  flags: number;
  /** Fastest downward speed since leaving the ground (m/s). */
  fallSpeed: number;
  valid: boolean;
}

export const newMotionState = (): MotionState => ({ flags: 0, fallSpeed: 0, valid: false });

/**
 * Movement cues of another player, from the change of their snapshot flags between two frames
 * (jump, landing, slide, wall run, mantle). Appends to `out` and advances `s`.
 */
export function motionCues(s: MotionState, flags: number, vy: number, out: MovementKind[]): void {
  const prev = s.flags;
  if (s.valid && (prev & PlayerFlags.ALIVE) !== 0 && (flags & PlayerFlags.ALIVE) !== 0) {
    const wasGround = (prev & PlayerFlags.ON_GROUND) !== 0;
    const ground = (flags & PlayerFlags.ON_GROUND) !== 0;
    if (wasGround && !ground && vy > MIN_JUMP_SPEED && (flags & PlayerFlags.MANTLING) === 0) out.push("jump");
    if (!wasGround && ground && s.fallSpeed > MIN_LAND_SPEED) out.push(s.fallSpeed > HEAVY_LAND_SPEED ? "landHeavy" : "land");
    const sliding = (flags & PlayerFlags.SLIDING) !== 0;
    const wasSliding = (prev & PlayerFlags.SLIDING) !== 0;
    if (sliding && !wasSliding) out.push("slideStart");
    else if (!sliding && wasSliding) out.push("slideStop");
    if ((flags & WALLRUN) !== 0 && (prev & WALLRUN) === 0) out.push("wallrunStart");
    if ((flags & PlayerFlags.MANTLING) !== 0 && (prev & PlayerFlags.MANTLING) === 0) out.push("mantle");
  }
  s.fallSpeed = (flags & PlayerFlags.ON_GROUND) !== 0 ? 0 : Math.max(s.fallSpeed, -vy);
  s.flags = flags;
  s.valid = true;
}

/** Shortest reload that ends with the rack: a cancelled reload (switch, melee) is not heard to finish. */
export const MIN_RELOAD_SEC = 0.6;

export interface HandlingState {
  flags: number;
  weapon: string;
  /** Time the reload started (ms), or -1. */
  reloadStartMs: number;
  valid: boolean;
}

export const newHandlingState = (): HandlingState => ({ flags: 0, weapon: "", reloadStartMs: -1, valid: false });

/**
 * Weapon handling of another player: reload start (mag out), reload end (rack, only when the
 * reload ran its course) and a weapon switch (equip). Appends to `out` and advances `s`.
 */
export function handlingCues(s: HandlingState, flags: number, weapon: string, nowMs: number, out: ReloadPhase[]): void {
  const alive = (flags & PlayerFlags.ALIVE) !== 0;
  const reloading = alive && (flags & PlayerFlags.RELOADING) !== 0;
  if (s.valid && alive && (s.flags & PlayerFlags.ALIVE) !== 0) {
    const was = s.reloadStartMs >= 0;
    const switched = s.weapon !== "" && weapon !== "" && weapon !== s.weapon;
    if (switched) out.push("equip");
    else if (reloading && !was) out.push("magOut");
    else if (!reloading && was && nowMs - s.reloadStartMs >= MIN_RELOAD_SEC * 1000) out.push("rack");
  }
  s.reloadStartMs = reloading ? (s.reloadStartMs >= 0 ? s.reloadStartMs : nowMs) : -1;
  s.flags = flags;
  s.weapon = weapon;
  s.valid = true;
}

// ---- Own shell reload ---------------------------------------------------------------------------

/**
 * A per-shell reload (shotgun) inserts one shell per cycle, and only the start and the end of the
 * reload are events. The rounds going up while the action is (or just was) a shell reload on the
 * same weapon is an insertion: one click per shell. The last shell lands in the tick the action
 * ends, so the previous tick's action counts too.
 */
export class ShellInsertCue {
  private ammo = -1;
  private slot = -1;
  private shellAction = false;

  reset(): void {
    this.ammo = -1;
  }

  /** Feed every predicted tick; true when a shell went in. */
  update(slot: number, ammo: number, action: string): boolean {
    const inserted = this.ammo >= 0 && slot === this.slot && ammo > this.ammo && (action === "reload_shell" || this.shellAction);
    this.ammo = ammo;
    this.slot = slot;
    this.shellAction = action === "reload_shell";
    return inserted;
  }
}

// ---- Thrown equipment ---------------------------------------------------------------------------

export interface ProjectileCue {
  cue: "throw" | "bounce";
  pos: Vec3Like;
}

/** Gravity of thrown equipment by kind (m/s^2, `EquipmentDef::gravity`). */
const PROJECTILE_GRAVITY: Readonly<Record<string, number>> = { throwing_knife: 9 };
const DEFAULT_PROJECTILE_GRAVITY = 22;
/** A projectile first seen faster than this has just been thrown. */
const THROW_SPEED = 8;
/** Velocity change beyond gravity (m/s, or this fraction of the speed) that counts as a hit on a surface. */
const BOUNCE_DELTA = 2.5;
const BOUNCE_FRACTION = 0.2;
/** Slower than this a grenade is rolling, not bouncing. */
const MIN_BOUNCE_SPEED = 2;
/** Bounces of one projectile closer together than this many ticks (150 ms) are one sound. */
const MIN_BOUNCE_GAP_TICKS = wholeTicks(0.15);

interface Tracked {
  vx: number;
  vy: number;
  vz: number;
  tick: number;
  lastBounce: number;
}

/**
 * Throw and bounce cues from the projectile lists of successive snapshots: a new fast projectile
 * is a throw, a velocity change that gravity does not explain is a bounce.
 */
export class ProjectileAudio {
  private readonly seen = new Map<number, Tracked>();
  private readonly alive = new Set<number>();

  clear(): void {
    this.seen.clear();
  }

  /** `tick` is the snapshot tick of `list`. Own throws (`owner === myId`) play from the prediction, not here. */
  update(list: readonly ProjectileSnapData[], tick: number, myId: number, out: ProjectileCue[]): void {
    const alive = this.alive;
    alive.clear();
    for (const p of list) {
      alive.add(p.id);
      const prev = this.seen.get(p.id);
      if (!prev) {
        this.seen.set(p.id, { vx: p.vel.x, vy: p.vel.y, vz: p.vel.z, tick, lastBounce: -1e9 });
        if (p.owner !== myId && Math.hypot(p.vel.x, p.vel.y, p.vel.z) > THROW_SPEED) out.push({ cue: "throw", pos: p.pos });
        continue;
      }
      const dt = (tick - prev.tick) * TICK_DT;
      if (dt > 0) {
        const g = PROJECTILE_GRAVITY[p.kind] ?? DEFAULT_PROJECTILE_GRAVITY;
        const speed = Math.hypot(prev.vx, prev.vy, prev.vz);
        const dv = Math.hypot(p.vel.x - prev.vx, p.vel.y - (prev.vy - g * dt), p.vel.z - prev.vz);
        if (speed > MIN_BOUNCE_SPEED && dv > Math.max(BOUNCE_DELTA, BOUNCE_FRACTION * speed) && tick - prev.lastBounce >= MIN_BOUNCE_GAP_TICKS) {
          prev.lastBounce = tick;
          out.push({ cue: "bounce", pos: p.pos });
        }
      }
      prev.vx = p.vel.x;
      prev.vy = p.vel.y;
      prev.vz = p.vel.z;
      prev.tick = tick;
    }
    for (const id of this.seen.keys()) if (!alive.has(id)) this.seen.delete(id);
  }
}
